// src/api/wpGraphql.js
import { WP_BASE } from "../lib/http";
import {
  wpGetTokens,
  wpGetAuthSession,
  wpIsCurrentAuthSession,
  wpRefreshAuthToken,
  wpLogout,
} from "./wpAuth";

const WP_GQL_URL = `${WP_BASE.replace(/\/+$/, "")}/graphql`;

/* ------------------------------ redaction ------------------------------ */

const REDACT_KEYS = new Set([
  "password",
  "pass",
  "pwd",
  "newPassword",
  "oldPassword",
  "confirmPassword",

  // ✅ OTP-related fields
  "emailOtp",
  "otp",
  "otpCode",
  "verificationCode",
  "code",

  // ✅ Reset-password token fields
  "token",
  "resetToken",
  "pwResetToken",
]);

function redactDeep(value) {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(redactDeep);

  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (REDACT_KEYS.has(k)) out[k] = "*****";
      else out[k] = redactDeep(v);
    }
    return out;
  }
  return value;
}

function redactText(raw) {
  const s = String(raw || "");
  if (!s) return s;

  const jsonPassword = /("password"\s*:\s*")([^"]*)(")/gi;
  const jsonPwd2 = /("newPassword"\s*:\s*")([^"]*)(")/gi;
  const jsonOld = /("oldPassword"\s*:\s*")([^"]*)(")/gi;
  const jsonConfirm = /("confirmPassword"\s*:\s*")([^"]*)(")/gi;
  const jsonEmailOtp = /("emailOtp"\s*:\s*")([^"]*)(")/gi;
  const jsonCode = /("code"\s*:\s*")([^"]*)(")/gi;

  // ✅ reset token patterns
  const jsonToken = /("token"\s*:\s*")([^"]*)(")/gi;
  const jsonResetToken = /("resetToken"\s*:\s*")([^"]*)(")/gi;
  const jsonPwResetToken = /("pwResetToken"\s*:\s*")([^"]*)(")/gi;

  const urlEncoded = /(\bpassword=)([^&\s]+)/gi;
  const urlEncodedToken = /(\btoken=)([^&\s]+)/gi;

  return s
    .replace(jsonPassword, '$1*****$3')
    .replace(jsonPwd2, '$1*****$3')
    .replace(jsonOld, '$1*****$3')
    .replace(jsonConfirm, '$1*****$3')
    .replace(jsonEmailOtp, '$1*****$3')
    .replace(jsonCode, '$1*****$3')
    .replace(jsonToken, '$1*****$3')
    .replace(jsonResetToken, '$1*****$3')
    .replace(jsonPwResetToken, '$1*****$3')
    .replace(urlEncoded, "$1*****")
    .replace(urlEncodedToken, "$1*****");
}

function buildGraphqlBody({ query, variables, operationName }) {
  const safeVars =
    variables && typeof variables === "object" && !Array.isArray(variables)
      ? variables
      : {};

  const body = { query, variables: safeVars };
  if (operationName) body.operationName = operationName;
  return body;
}

async function fetchGraphql({
  query,
  variables,
  token,
  operationName,
  debugTag,
}) {
  if (!WP_GQL_URL) throw new Error("Missing WP GraphQL endpoint (WP_BASE).");

  const headers = { "Content-Type": "application/json" };
  const t = String(token || "").trim();
  if (t) headers.Authorization = `Bearer ${t}`;

  const body = buildGraphqlBody({ query, variables, operationName });

  if (debugTag) {
    try {
      console.log(`[WP_GQL:${debugTag}] operationName=`, operationName || "");
      console.log(
        `[WP_GQL:${debugTag}] variables=`,
        redactDeep(body.variables)
      );
    } catch (_) {}
  }

  const res = await fetch(WP_GQL_URL, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch (_) {
    json = null;
  }

  return { res, text, json };
}

function normalizeGraphqlErrors(json) {
  const errs = Array.isArray(json?.errors) ? json.errors : [];
  const msg = errs
    .map((e) => String(e?.message || ""))
    .filter(Boolean)
    .join(" | ");
  return { errs, msg };
}

/* =========================================================
   Register special-case (legacy safety)
========================================================= */

function shouldTreatRegisterAsSideEffect({ status, json, requestTag }) {
  if (requestTag !== "register") return false;
  if (status >= 500) return true;

  const hasErrors = Array.isArray(json?.errors) && json.errors.length > 0;
  const hasData = !!json?.data;

  if (hasErrors && hasData) return true;
  return false;
}

function isRegisterOkEvenIf400({ requestTag, status, json }) {
  if (requestTag !== "register") return false;
  if (!json?.data?.sehoRegisterCustomer) return false;

  const ok = json.data.sehoRegisterCustomer.ok === true;
  if (ok && status >= 400 && status < 500) return true;

  return false;
}

/* =========================================================
   Auth error detection
========================================================= */

function looksLikeAuthErrorFromGraphqlErrors(errs) {
  if (!Array.isArray(errs) || !errs.length) return false;

  for (const e of errs) {
    const msg = String(e?.message || "").toLowerCase();
    const code = String(e?.extensions?.code || "").toUpperCase();
    const category = String(e?.extensions?.category || "").toUpperCase();

    // Strong signals from WPGraphQL
    if (category === "AUTHENTICATION" || code === "UNAUTHENTICATED")
      return true;

    // Very specific token/JWT failure phrases
    if (
      msg.includes("invalid token") ||
      msg.includes("expired token") ||
      msg.includes("jwt") ||
      msg.includes("not authenticated") ||
      msg.includes("unauthorized")
    ) {
      return true;
    }
  }

  return false;
}

function looksLikeAuthErrorFromText(messageOrText) {
  const lower = String(messageOrText || "").toLowerCase();
  return (
    lower.includes("jwt") ||
    lower.includes("not authenticated") ||
    lower.includes("unauthorized") ||
    lower.includes("expired token") ||
    lower.includes("invalid token")
  );
}

/**
 * Requests that are expected to be unauthenticated OR should never cause refresh/logout loops.
 * ✅ OTP flows (including authed ones) should NOT trigger refresh/logout loops.
 * ✅ Forgot/reset flows should NOT trigger refresh/logout loops.
 */
function isPublicAuthFlow(requestTag, operationName) {
  const tag = String(requestTag || "").toLowerCase();
  const op = String(operationName || "").toLowerCase();

  // Known public flows
  if (tag === "login" || tag === "login2") return true;
  if (tag === "register") return true;
  if (tag === "refresh") return true;

  // ✅ OTP flows
  if (
    tag === "emailotp" ||
    tag === "requestemailotp" ||
    tag === "verifyemailotp" ||
    tag === "otp" ||
    tag === "sendotp"
  )
    return true;

  // ✅ NEW: forgot/reset flows
  if (
    tag === "forgotpassword" ||
    tag === "forgot_username" ||
    tag === "forgotusername" ||
    tag === "resetpassword" ||
    tag === "reset_password" ||
    tag === "pwreset" ||
    tag === "passwordreset"
  )
    return true;

  // Generic safety: if operationName hints it
  if (op.includes("login")) return true;
  if (op.includes("register")) return true;
  if (op.includes("refresh")) return true;

  // ✅ OTP op name hints
  if (op.includes("emailotp")) return true;
  if (op.includes("requestemailotp")) return true;
  if (op.includes("verifyemailotp")) return true;
  if (op.includes("sehorequestemailotp")) return true;
  if (op.includes("sehoverifyemailotp")) return true;

  // ✅ NEW: forgot/reset op name hints
  if (op.includes("sehoforgotpassword")) return true;
  if (op.includes("sehoforgotusername")) return true;
  if (op.includes("sehoresetpasswordwithtoken")) return true;
  if (op.includes("forgotpassword")) return true;
  if (op.includes("forgotusername")) return true;
  if (op.includes("resetpassword")) return true;

  return false;
}

/* =========================================================
   Session-aware single-flight refresh lock
========================================================= */

let refreshInFlight = null;

/**
 * Check whether a request still belongs to the authentication
 * session that originally started it.
 *
 * The token comparison is retained only as a fallback for an older,
 * incomplete session that does not yet contain a session ID.
 */
function requestStillOwnsAuthSession({
  sessionId,
  authToken,
} = {}) {
  const normalizedSessionId =
    String(sessionId || "").trim();

  if (normalizedSessionId) {
    return wpIsCurrentAuthSession(
      normalizedSessionId,
    );
  }

  const currentAuthToken =
    String(
      wpGetTokens()?.authToken || "",
    )
      .replace(/^Bearer\s+/i, "")
      .trim();

  const expectedAuthToken =
    String(authToken || "")
      .replace(/^Bearer\s+/i, "")
      .trim();

  return Boolean(
    expectedAuthToken &&
      currentAuthToken ===
        expectedAuthToken,
  );
}

async function refreshSingleFlight({
  debug,
  sessionId,
}) {
  const normalizedSessionId =
    String(sessionId || "").trim();

  if (
    !normalizedSessionId ||
    !wpIsCurrentAuthSession(
      normalizedSessionId,
    )
  ) {
    throw new Error(
      "The authentication request belongs to an inactive session",
    );
  }

  if (
    refreshInFlight?.sessionId ===
    normalizedSessionId
  ) {
    return refreshInFlight.promise;
  }

  const refreshEntry = {
    sessionId: normalizedSessionId,
    promise: null,
  };

  refreshEntry.promise =
    (async () => {
      try {
        if (
          !wpIsCurrentAuthSession(
            normalizedSessionId,
          )
        ) {
          throw new Error(
            "The authentication session was replaced before refresh",
          );
        }

        return await wpRefreshAuthToken({
          debug,
        });
      } finally {
        /*
         * An older refresh must not clear the lock belonging to a
         * newer session.
         */
        if (
          refreshInFlight ===
          refreshEntry
        ) {
          refreshInFlight = null;
        }
      }
    })();

  refreshInFlight = refreshEntry;

  return refreshEntry.promise;
}

/* =========================================================
   Custom error to preserve GraphQL error details
========================================================= */

class WpGraphqlError extends Error {
  constructor(message, meta = {}) {
    super(String(message || "GraphQL error"));
    this.name = "WpGraphqlError";
    this.__status = meta.status || 0;
    this.__errs = meta.errs || [];
    this.__rawText = meta.rawText || "";
    this.__data = meta.data || null;
    this.__json = meta.json || null;
  }
}

/* =========================================================
   ✅ NEW: normalize thrown error so UI can read details
========================================================= */

function toPlainGraphqlError(err) {
  const e = new Error(redactText(String(err?.message || "GraphQL error")));
  e.name = "WpGraphqlError";

  if (err && typeof err === "object") {
    if (typeof err.__status === "number") e.__status = err.__status;
    if (Array.isArray(err.__errs)) e.__errs = err.__errs;
    if (typeof err.__rawText === "string")
      e.__rawText = redactText(err.__rawText);
    if (err.__data != null) e.__data = err.__data;
    if (err.__json != null) e.__json = err.__json;
  }
  return e;
}

/* =========================================================
   ✅ NEW: small helper for register payload mismatch
   - Some older pages still send `birthdate`, backend expects `birthday`.
========================================================= */

function normalizeRegisterVariables(variables) {
  const v = variables && typeof variables === "object" ? { ...variables } : {};
  const maybeInput = v.input && typeof v.input === "object" ? { ...v.input } : null;

  const fixShape = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const out = { ...obj };

    if (out.birthdate && !out.birthday) {
      out.birthday = out.birthdate;
      delete out.birthdate;
    }
    if (out.birthday === "") delete out.birthday;
    if (out.gender === "") delete out.gender;

    return out;
  };

  if (maybeInput) {
    v.input = fixShape(maybeInput);
    return v;
  }

  return fixShape(v);
}

/* =========================================================
   ✅ NEW: allow register to be treated as success marker safely
========================================================= */

function tryReturnRegisterOkData({ requestTag, status, json }) {
  if (requestTag !== "register") return null;

  const payload = json?.data?.sehoRegisterCustomer;
  if (!payload) return null;

  if (payload?.ok === true) return json.data;
  if (payload?.success === true) return json.data;

  return null;
}

/* =========================================================
   Main API
========================================================= */

export async function wpGraphql(query, variables = {}, opts = {}) {
  const {
    token,
    requestTag = "graphql",
    operationName,
    retryOnAuth = true,
    debug = false,
    allowRegisterSideEffect = false,
  } = opts;

  const log = (...args) => {
    if (debug) console.log(`[wpGraphql:${requestTag}]`, ...args);
  };

  const stored = wpGetTokens();

  const storedAuthToken =
    String(
      stored?.authToken || "",
    )
      .replace(/^Bearer\s+/i, "")
      .trim();

  const useToken =
    token !== undefined
      ? String(token || "")
          .replace(
            /^Bearer\s+/i,
            "",
          )
          .trim()
      : storedAuthToken;

  /*
   * Capture the browser session that owns this token.
   *
   * This also supports functions that explicitly pass the current
   * stored token instead of letting wpGraphql read it automatically.
   */
  const sessionAtStart =
    useToken &&
    useToken === storedAuthToken
      ? wpGetAuthSession()
      : null;

  const requestSessionId =
    String(
      sessionAtStart?.sessionId ||
        "",
    ).trim();

  // ✅ Normalize register variables here so old pages won't break
  const useVars =
    String(requestTag || "").toLowerCase() === "register"
      ? normalizeRegisterVariables(variables)
      : variables;

  const markerReturn = ({ status, text, json }) => ({
    __seho_registerSideEffectLikely: true,
    __httpStatus: status,
    __rawText: debug ? redactText(text) : undefined,
    __errors: json?.errors || null,
    __data: json?.data || null,
  });

  const attempt = async (tok, attemptNo) => {
    const { res, text, json } = await fetchGraphql({
      query,
      variables: useVars,
      token: tok,
      operationName,
      debugTag: debug ? requestTag : "",
    });

    const status = res.status;
    log("HTTP", status, "attempt", attemptNo);

    if (!json) {
      if (allowRegisterSideEffect && requestTag === "register" && status >= 400) {
        log("Register likely side-effect: non-JSON error response");
        return markerReturn({ status, text, json });
      }
      const snippet = redactText(String(text || "").slice(0, 300).trim());
      throw new WpGraphqlError(
        snippet ? `HTTP ${status}: ${snippet}` : `HTTP ${status}`,
        { status, rawText: text }
      );
    }

    // ✅ If register returns ok=true, accept it immediately even if 4xx / warnings
    const registerOkData = tryReturnRegisterOkData({ requestTag, status, json });
    if (registerOkData) {
      if (status >= 400 && status < 500) {
        log("Register ok=true with HTTP 4xx; treating as success.");
      }
      return registerOkData;
    }

    const { errs, msg: gqlErrMsg } = normalizeGraphqlErrors(json);

    if (isRegisterOkEvenIf400({ requestTag, status, json })) {
      log("Register returned ok=true with HTTP 4xx; treating as success.");
      return json.data;
    }

    if (
      allowRegisterSideEffect &&
      shouldTreatRegisterAsSideEffect({ status, json, requestTag })
    ) {
      log("Register side-effect likely (status/errors). Returning marker object.");
      return markerReturn({ status, text, json });
    }

    // Successful HTTP
    if (status >= 200 && status < 300) {
      if (errs.length) {
        throw new WpGraphqlError(redactText(gqlErrMsg || "GraphQL error"), {
          status,
          errs,
          rawText: text,
          data: json?.data || null,
          json,
        });
      }
      return json?.data;
    }

    // Non-2xx: if GraphQL errors exist, throw them (keep errs for auth detection)
    if (errs.length) {
      throw new WpGraphqlError(redactText(gqlErrMsg || `HTTP ${status}`), {
        status,
        errs,
        rawText: text,
        data: json?.data || null,
        json,
      });
    }

    const snippet = redactText(String(text || "").slice(0, 300).trim());
    throw new WpGraphqlError(
      snippet ? `HTTP ${status}: ${snippet}` : `HTTP ${status}`,
      { status, rawText: text, data: json?.data || null, json }
    );
  };

  try {
    return await attempt(useToken, 1);
  } catch (err) {
    const isWpErr = err && err.name === "WpGraphqlError";

    const msg = redactText(String(err?.message || ""));
    const lower = msg.toLowerCase();

    const gqlErrs = isWpErr ? err.__errs : [];
    const authFromGql = looksLikeAuthErrorFromGraphqlErrors(gqlErrs);
    const authFromText = looksLikeAuthErrorFromText(lower);

    // ✅ Public flows should never trigger refresh/logout loops
    if (isPublicAuthFlow(requestTag, operationName)) {
      throw isWpErr ? toPlainGraphqlError(err) : new Error(msg);
    }

    const isAuthish = authFromGql || authFromText;

    if (retryOnAuth && isAuthish) {
      /*
       * The request may have failed after the user already logged in
       * again. In that case, its old authentication failure must not
       * refresh or clear the new session.
       */
      if (
        !requestStillOwnsAuthSession({
          sessionId:
            requestSessionId,

          authToken:
            useToken,
        })
      ) {
        log(
          "Ignoring authentication failure from an older session.",
        );

        throw isWpErr
          ? toPlainGraphqlError(err)
          : new Error(msg);
      }

      log(
        "Looks like auth error; trying a session-safe refresh then retry.",
      );

      try {
        const newTok =
          await refreshSingleFlight({
            debug,
            sessionId:
              requestSessionId,
          });

        if (
          !requestStillOwnsAuthSession(
            {
              sessionId:
                requestSessionId,

              authToken:
                useToken,
            },
          )
        ) {
          throw new Error(
            "The authentication session was replaced before retry",
          );
        }

        const retryData =
          await attempt(newTok, 2);

        /*
         * Do not return results produced for a session that was
         * replaced while the retry request was running.
         */
        if (
          !requestStillOwnsAuthSession(
            {
              sessionId:
                requestSessionId,

              authToken:
                newTok,
            },
          )
        ) {
          throw new Error(
            "The authentication session was replaced during retry",
          );
        }

        return retryData;
      } catch (refreshError) {
        /*
         * Logout only when the session that originally failed is
         * still the current browser session.
         */
        if (
          requestStillOwnsAuthSession(
            {
              sessionId:
                requestSessionId,

              authToken:
                useToken,
            },
          )
        ) {
          log(
            "Refresh and retry failed for the current session; logging it out.",
            refreshError,
          );

          wpLogout({
            reason: "expired",

            message:
              "Session expired or invalid. Please login again.",

            sessionId:
              requestSessionId,
          });
        } else {
          log(
            "Refresh failed for an older session; the current session remains active.",
            refreshError,
          );
        }

        throw isWpErr
          ? toPlainGraphqlError(err)
          : new Error(msg);
      }
    }

    if (isAuthish) {
      if (
        requestStillOwnsAuthSession({
          sessionId:
            requestSessionId,

          authToken:
            useToken,
        })
      ) {
        wpLogout({
          reason: "expired",

          message:
            "Session expired or invalid.",

          sessionId:
            requestSessionId,
        });
      } else {
        log(
          "Ignoring non-retry authentication failure from an older session.",
        );
      }
    }

    throw isWpErr
      ? toPlainGraphqlError(err)
      : new Error(msg);
  }
}
